/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { getOrCreateMapTreeNode, getSchemaAndPolicy, } from "../feature-libraries/index.js";
import { getTreeNodeForField, prepareContentForHydration, } from "./proxies.js";
import { getOrCreateInnerNode } from "./proxyBinding.js";
import { getSimpleNodeSchema } from "./core/index.js";
import { createFieldSchema, FieldKind, } from "./schemaTypes.js";
import { NodeKind, 
// eslint-disable-next-line import/no-deprecated
typeNameSymbol, typeSchemaSymbol, } from "./core/index.js";
import { mapTreeFromNodeData } from "./toMapTree.js";
import { getFlexSchema } from "./toFlexSchema.js";
import { brand, count } from "../util/index.js";
import { TreeNodeValid } from "./treeNodeValid.js";
const handler = {
    getPrototypeOf: () => {
        return Map.prototype;
    },
};
class CustomMapNodeBase extends TreeNodeValid {
    [Symbol.iterator]() {
        return this.entries();
    }
    delete(key) {
        const field = getOrCreateInnerNode(this).getBoxed(key);
        field.editor.set(undefined, field.length === 0);
    }
    *entries() {
        const node = getOrCreateInnerNode(this);
        for (const key of node.keys()) {
            yield [
                key,
                getTreeNodeForField(node.getBoxed(key)),
            ];
        }
    }
    get(key) {
        const node = getOrCreateInnerNode(this);
        const field = node.getBoxed(key);
        return getTreeNodeForField(field);
    }
    has(key) {
        return getOrCreateInnerNode(this).tryGetField(brand(key)) !== undefined;
    }
    keys() {
        const node = getOrCreateInnerNode(this);
        return node.keys();
    }
    set(key, value) {
        const node = getOrCreateInnerNode(this);
        const classSchema = getSimpleNodeSchema(node.schema);
        const mapTree = mapTreeFromNodeData(value, createFieldSchema(FieldKind.Optional, classSchema.info), node.context?.nodeKeyManager, getSchemaAndPolicy(node));
        const field = node.getBoxed(key);
        if (node.context !== undefined) {
            prepareContentForHydration(mapTree, node.context.checkout.forest);
        }
        field.editor.set(mapTree, field.length === 0);
        return this;
    }
    get size() {
        return count(getOrCreateInnerNode(this).keys());
    }
    *values() {
        for (const [, value] of this.entries()) {
            yield value;
        }
    }
    forEach(callbackFn, thisArg) {
        for (const field of getOrCreateInnerNode(this).boxedIterator()) {
            const node = getTreeNodeForField(field);
            callbackFn.call(thisArg, node, field.key, this);
        }
    }
}
CustomMapNodeBase.kind = NodeKind.Map;
/**
 * Define a {@link TreeNodeSchema} for a {@link (TreeArrayNode:interface)}.
 *
 * @param base - base schema type to extend.
 * @param useMapPrototype - should this type emulate a ES6 Map object (by faking its prototype with a proxy).
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function mapSchema(identifier, info, implicitlyConstructable, useMapPrototype) {
    let flexSchema;
    class schema extends CustomMapNodeBase {
        static prepareInstance(instance, flexNode) {
            if (useMapPrototype) {
                return new Proxy(instance, handler);
            }
            return instance;
        }
        static buildRawNode(instance, input) {
            return getOrCreateMapTreeNode(flexSchema, mapTreeFromNodeData(input, this));
        }
        static oneTimeSetup() {
            flexSchema = getFlexSchema(this);
        }
        // eslint-disable-next-line import/no-deprecated
        get [typeNameSymbol]() {
            return identifier;
        }
        get [typeSchemaSymbol]() {
            return schema.constructorCached?.constructor;
        }
    }
    schema.constructorCached = undefined;
    schema.identifier = identifier;
    schema.info = info;
    schema.implicitlyConstructable = implicitlyConstructable;
    const schemaErased = schema;
    return schemaErased;
}
//# sourceMappingURL=mapNode.js.map